import { type RequestFailure, type ApiResponse } from '@owl-nest/api-client'
import * as model from '@owl-nest/models'
import { Either } from '@owl-nest/monad'
import { Status, type QueryAction } from '@owl-nest/redux-wrapper'
import * as Enum from '@owl-nest/legacy-enum'

import { type RootState } from '../reducers'
import * as impactService from '../services/impact'

export const TYPE = Enum.create({
  LOAD_REQUEST: 'IMPACT.LOAD_REQUEST',
  LOAD_SUCCESS: 'IMPACT.LOAD_SUCCESS',
  LOAD_FAILURE: 'IMPACT.LOAD_FAILURE',
  SET: 'IMPACT.SET',
})

type LoadFailure = { type: typeof TYPE.LOAD_FAILURE; failure: RequestFailure }
type LoadRequest = { type: typeof TYPE.LOAD_REQUEST; projectId: number }
type LoadSuccess = { type: typeof TYPE.LOAD_SUCCESS; success: model.impact.Impact }

export function load(
  projectId: number,
  projectToken?: string,
): QueryAction<
  Promise<ApiResponse<model.impact.Impact> | undefined>,
  RootState,
  LoadFailure | LoadSuccess | LoadRequest
> {
  return {
    type: 'query',
    query: async (dispatch, selector) => {
      const impactState = selector((state) => state.application.impact)
      const isLoggedIn = selector((state) => state.user.identity.type === 'loggedin')

      // if a request is already pending for the same projectId, don't send another request
      if (impactState.status.load.status === Status.PENDING && impactState.status.load.projectId === projectId) {
        return
      }

      dispatch.request({ type: TYPE.LOAD_REQUEST, projectId })

      const result = await impactService.query(projectId, isLoggedIn, projectToken)

      return result.caseOf({
        left: (failure) => {
          dispatch.failure({ type: TYPE.LOAD_FAILURE, failure })
          return Either.left(failure)
        },
        right: (success) => {
          dispatch.success({ type: TYPE.LOAD_SUCCESS, success })
          return Either.right(success)
        },
      })
    },
  }
}

type Set = { type: typeof TYPE.SET; impact: model.impact.Impact }

export function set(impact: model.impact.Impact): Set {
  return { type: TYPE.SET, impact }
}

export type Action = LoadRequest | LoadSuccess | LoadFailure | Set
